import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";

import {
  AddCircleRounded,
  ArrowDownward,
  ArrowUpward,
  CloseRounded,
  DeleteRounded,
  EditRounded,
  InfoRounded,
  LocationCityRounded,
  PlaceRounded,
  Search,
  SettingsRounded,
  UnfoldMore,
  WarningAmberRounded,
} from "@mui/icons-material";

import { api } from "@api/api";

import PaginacaoLista from "../../../componentes/comuns/PaginacaoLista";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";
import { BuildingsFillIcon } from "../../../componentes/ItemList";

import {
  botaoAdicionar,
  botaoCancelar,
  botaoExcluir,
  botaoSalvar,
} from "../../../estilos/botoes";

import {
  coresTextosGlobais, iconesGlobais,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
  tamanhosModaisGlobais,
} from "../../../estilos/global";
import Tooltip from "@mui/material/Tooltip";

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

const ESTILO_BOTAO_ADICIONAR_CIDADE = {
  ...botaoAdicionar,
  fontSize: tamanhosControlesGlobais.botao,
};

const ESTILO_ACAO_EDITAR = {
  width: 30,
  height: 30,
  p: 0,
  color: "var(--sear-azul, #087cf0)",
  backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
  border: "1px solid var(--sear-borda, #d7eaff)",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    color: "var(--sear-azul, #0767d8)",
    backgroundColor: "var(--sear-superficie-azul, #dceeff)",
    transform: "translateY(-1px)",
  },
};

const ESTILO_ACAO_EXCLUIR = {
  width: 30,
  height: 30,
  p: 0,
  color: "var(--sear-erro-texto, #e53935)",
  backgroundColor: "var(--sear-erro-fundo, #ffeded)",
  border: "1px solid var(--sear-erro-borda, #ffdcdc)",
  borderRadius: "50%",
  transition:
    "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    color: "var(--sear-erro-texto, #c62828)",
    backgroundColor: "var(--sear-erro-fundo, #ffdddd)",
    transform: "translateY(-1px)",
  },
};

const ESTILO_PAPEL_MODAL = {
  borderRadius: 2,
  borderTop: "4px solid var(--sear-borda-forte, #087cf0)",
  overflow: "hidden",
  "& .MuiAlert-message": {
    fontSize: tamanhosModaisGlobais.textoAuxiliar,
  },
};

const ESTILO_ICONE_TITULO_MODAL = {
  width: 42,
  height: 42,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  borderRadius: 1.5,
  color: "var(--sear-azul, #087cf0)",
  backgroundColor: "var(--sear-superficie-azul, #e1f1ff)",
  border: "1px solid var(--sear-borda, #d1e8fc)",
};

const ESTILO_CARD_MODAL = {
  border: "1px solid var(--sear-borda, #d7e7f7)",
  borderRadius: 2,
  overflow: "hidden",
  backgroundColor: "var(--sear-superficie, #ffffff)",
};

const ESTILO_CABECALHO_CARD_MODAL = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  px: 2,
  py: 1.35,
  borderBottom: "1px solid var(--sear-borda, #e4edf6)",
  background: "linear-gradient(90deg, var(--sear-superficie-azul, #eef7ff) 0%, var(--sear-superficie-azul, #f8fbff) 100%)",
};

const ESTILO_ICONE_CARD_MODAL = {
  width: 40,
  height: 40,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  borderRadius: 1.5,
  color: "var(--sear-azul, #087cf0)",
  backgroundColor: "var(--sear-superficie-azul, #e1f1ff)",
  border: "1px solid var(--sear-borda, #d1e8fc)",
};

const ESTILO_CAMPO_MODAL = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    borderRadius: 1.15,
    backgroundColor: "var(--sear-superficie-suave, #f4f6f8)",
    fontSize: tamanhosModaisGlobais.campo,
    color: "var(--sear-texto-secundario, #40546d)",
    "& fieldset": { borderColor: "var(--sear-borda, #dce4ec)" },
    "&:hover fieldset": { borderColor: "var(--sear-borda, #b7cde3)" },
    "&.Mui-focused fieldset": { borderColor: "var(--sear-borda-forte, #087cf0)" },
  },
  "& .MuiInputBase-input": { py: 1.05 },
  "& .MuiInputLabel-root": {
    fontSize: tamanhosModaisGlobais.rotulo,
    "&.MuiInputLabel-shrink": {
      fontSize: `calc(${tamanhosModaisGlobais.rotulo} / 0.75)`,
    },
  },
  "& .MuiOutlinedInput-notchedOutline legend": {
    fontSize: tamanhosModaisGlobais.rotulo,
  },
  "& .MuiFormHelperText-root": {
    fontSize: tamanhosModaisGlobais.textoAuxiliar,
  },
};

export default function CitiesPage() {
  const [cidades, setCidades] = useState([]);
  const [sres, setSres] = useState([]);

  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(5);

  const [ordem, setOrdem] = useState({
    coluna: "cidade",
    direcao: "asc",
  });

  const [carregando, setCarregando] = useState(true);
  const [erroCidades, setErroCidades] = useState("");
  const [erroSres, setErroSres] = useState(false);

  const [modalNovaCidade, setModalNovaCidade] = useState(false);
  const [nomeNovaCidade, setNomeNovaCidade] = useState("");
  const [sreSelecionada, setSreSelecionada] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erroFormulario, setErroFormulario] = useState("");

  const [cidadeParaEditar, setCidadeParaEditar] = useState(null);
  const [modalEdicaoAberto, setModalEdicaoAberto] = useState(false);
  const [nomeEdicao, setNomeEdicao] = useState("");
  const [sreEdicaoSelecionada, setSreEdicaoSelecionada] = useState("");
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [erroEdicao, setErroEdicao] = useState("");

  const [cidadeParaExcluir, setCidadeParaExcluir] = useState(null);
  const [confirmacaoExclusaoAberta, setConfirmacaoExclusaoAberta] =
    useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const [aviso, setAviso] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    setErroCidades("");
    setErroSres(false);

    const [resultadoCidades, resultadoSres] = await Promise.allSettled([
      axios.get(api.listCities(), { withCredentials: true }),
      axios.get(api.listSres(), { withCredentials: true }),
    ]);

    if (resultadoCidades.status === "fulfilled") {
      setCidades(
        Array.isArray(resultadoCidades.value.data)
          ? resultadoCidades.value.data
          : [],
      );
    } else {
      setErroCidades("Não foi possível carregar as cidades.");
    }

    if (resultadoSres.status === "fulfilled") {
      setSres(
        Array.isArray(resultadoSres.value.data) ? resultadoSres.value.data : [],
      );
    } else {
      setErroSres(true);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const sresAtivas = useMemo(
    () =>
      sres
        .filter((sre) => sre.active !== false)
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "pt-BR"),
        ),
    [sres],
  );

  const sresPorCidade = useMemo(() => {
    const mapa = new Map();

    sres.forEach((sre) => {
      if (!Array.isArray(sre.cities)) {
        return;
      }

      sre.cities.forEach((cidade) => {
        const id = Number(cidade.id);
        const nomes = mapa.get(id) || [];

        if (!nomes.includes(sre.name)) {
          mapa.set(id, [...nomes, sre.name]);
        }
      });
    });

    return mapa;
  }, [sres]);

  const obterSreDaCidade = useCallback(
    (cidadeId) =>
      sres.find((sre) =>
        Array.isArray(sre.cities)
          ? sre.cities.some((cidade) => Number(cidade.id) === Number(cidadeId))
          : false,
      ) || null,
    [sres],
  );

  const cidadesFiltradas = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase("pt-BR");

    return cidades
      .filter((cidade) => {
        if (!termo) {
          return true;
        }

        const nomeCidade = String(cidade.name || "").toLocaleLowerCase("pt-BR");
        const nomesSres = sresPorCidade.get(Number(cidade.id)) || [];

        return (
          nomeCidade.includes(termo) ||
          nomesSres.some((nome) =>
            String(nome).toLocaleLowerCase("pt-BR").includes(termo),
          )
        );
      })
      .sort((a, b) => {
        const obterValor = (cidade) =>
          ordem.coluna === "sre"
            ? (sresPorCidade.get(Number(cidade.id)) || []).join(", ")
            : String(cidade.name || "");

        const resultado = obterValor(a).localeCompare(obterValor(b), "pt-BR", {
          sensitivity: "base",
          numeric: true,
        });

        return ordem.direcao === "asc" ? resultado : -resultado;
      });
  }, [cidades, busca, sresPorCidade, ordem]);

  const ordenarPor = (coluna) => {
    setOrdem((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));

    setPagina(1);
  };

  const totalPaginas = Math.max(
    1,
    Math.ceil(cidadesFiltradas.length / itensPorPagina),
  );

  const paginaAtual = Math.min(pagina, totalPaginas);
  const indiceInicial = (paginaAtual - 1) * itensPorPagina;

  const indiceFinal = Math.min(
    indiceInicial + itensPorPagina,
    cidadesFiltradas.length,
  );

  const cidadesDaPagina = cidadesFiltradas.slice(indiceInicial, indiceFinal);

  useEffect(() => {
    setPagina((atual) => Math.min(atual, totalPaginas));
  }, [totalPaginas]);

  const abrirModalNovaCidade = () => {
    setNomeNovaCidade("");
    setSreSelecionada("");
    setErroFormulario("");
    setModalNovaCidade(true);
  };

  const fecharModalNovaCidade = () => {
    if (salvando) {
      return;
    }

    setModalNovaCidade(false);
  };

  const criarCidade = async (event) => {
    event.preventDefault();

    const nome = nomeNovaCidade.trim().replace(/\s+/g, " ");

    if (nome.length < 2) {
      setErroFormulario("Informe o nome da cidade.");
      return;
    }

    setSalvando(true);
    setErroFormulario("");

    try {
      await axios.post(
        sreSelecionada ? api.linkCityToSre(sreSelecionada) : api.createCity(),
        { name: nome },
        { withCredentials: true },
      );

      setModalNovaCidade(false);
      setBusca("");
      setPagina(1);

      await carregarDados();

      setAviso({
        open: true,
        message: sreSelecionada
          ? "Cidade cadastrada e vinculada à SRE com sucesso."
          : "Cidade cadastrada com sucesso. Você pode vinculá-la a uma SRE depois.",
        severity: "success",
      });
    } catch (error) {
      const mensagem = error.response?.data?.message;

      setErroFormulario(
        (Array.isArray(mensagem) ? mensagem.join(" ") : mensagem) ||
          "Não foi possível cadastrar a cidade.",
      );
    } finally {
      setSalvando(false);
    }
  };

  const abrirEdicao = (cidade) => {
    const sreAtual = obterSreDaCidade(cidade.id);

    setCidadeParaEditar(cidade);
    setNomeEdicao(cidade.name || "");
    setSreEdicaoSelecionada(sreAtual?.id || "");
    setErroEdicao("");
    setModalEdicaoAberto(true);
  };

  const fecharEdicao = () => {
    if (salvandoEdicao) {
      return;
    }

    setModalEdicaoAberto(false);
  };

  const salvarEdicao = async (event) => {
    event.preventDefault();

    if (!cidadeParaEditar || salvandoEdicao) {
      return;
    }

    const nome = nomeEdicao.trim().replace(/\s+/g, " ");

    if (nome.length < 2) {
      setErroEdicao("Informe o nome da cidade.");
      return;
    }

    setSalvandoEdicao(true);
    setErroEdicao("");

    try {
      await axios.put(
        api.updateCity(cidadeParaEditar.id),
        {
          name: nome,
          sreId: sreEdicaoSelecionada ? Number(sreEdicaoSelecionada) : null,
        },
        { withCredentials: true },
      );

      await carregarDados();

      setModalEdicaoAberto(false);

      setAviso({
        open: true,
        message: "Cidade atualizada com sucesso.",
        severity: "success",
      });
    } catch (error) {
      const mensagem = error.response?.data?.message;

      setErroEdicao(
        (Array.isArray(mensagem) ? mensagem.join(" ") : mensagem) ||
          "Não foi possível atualizar a cidade.",
      );
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const excluirCidade = async () => {
    if (!cidadeParaExcluir || excluindo) {
      return;
    }

    setExcluindo(true);

    try {
      await axios.delete(api.deleteCity(cidadeParaExcluir.id), {
        withCredentials: true,
      });

      setCidades((atuais) =>
        atuais.filter((cidade) => cidade.id !== cidadeParaExcluir.id),
      );

      setConfirmacaoExclusaoAberta(false);

      setAviso({
        open: true,
        message: "Cidade excluída com sucesso.",
        severity: "success",
      });
    } catch (error) {
      const mensagem = error.response?.data?.message;

      setAviso({
        open: true,
        message:
          (Array.isArray(mensagem) ? mensagem.join(" ") : mensagem) ||
          "Não foi possível excluir a cidade. Verifique se ela está em uso.",
        severity: "error",
      });
    } finally {
      setExcluindo(false);
    }
  };

  const renderCabecalhoOrdenavel = (titulo, coluna, Icone) => {
    const ativo = ordem.coluna === coluna;

    return (
      <TableSortLabel
        active={ativo}
        direction={ativo ? ordem.direcao : "asc"}
        onClick={() => ordenarPor(coluna)}
        IconComponent={
          ativo
            ? ordem.direcao === "asc"
              ? ArrowUpward
              : ArrowDownward
            : UnfoldMore
        }
        sx={{
          color: coresTextosGlobais.cabecalhoTabela,
          fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
          fontWeight: 700,
          "&:hover": { color: "var(--sear-azul, #0767d8)" },
          "&.Mui-active": { color: coresTextosGlobais.cabecalhoTabela },
          "& .MuiTableSortLabel-icon": {
            opacity: 1,
            color: "var(--sear-azul, #385b90)",
            fontSize: iconesGlobais.conteudoTabela.fontSize,
            ml: 0.35,
            transform: "none !important",
          },
        }}
      >
        <Box
          component="span"
          sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
        >
          <Icone
            sx={{
              color: "var(--sear-azul, #087cf0)",
              fontSize: iconesGlobais.tabela.fontSize,
              flexShrink: 0,
            }}
          />
          {titulo}
        </Box>
      </TableSortLabel>
    );
  };

  return (
    <Box
      component={TransicaoPagina}
      data-pagina-rolavel=""
      variante="navegacao"
      carregando={carregando && cidades.length === 0}
      carregamento={
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      }
      sx={{ width: "100%", py: 0 }}
    >
      {/* CABEÇALHO */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.4,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              borderRadius: 1.5,
              color: "var(--sear-azul, #087cf0)",
              backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",
            }}
          >
            <PlaceRounded sx={{ fontSize: 27 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                color: coresTextosGlobais.tituloPagina,
                fontSize: tamanhosPaginasAdministrativas.tituloPagina,
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              Cidades
            </Typography>

            <Typography
              sx={{
                mt: 0,
                color: "var(--sear-texto-secundario, #7b8798)",
                fontSize: tamanhosControlesGlobais.descricao,
                lineHeight: 1.35,
              }}
            >
              Gerencie os municípios disponíveis para vinculação às SREs.
            </Typography>
          </Box>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCircleRounded sx={{ fontSize: "18px !important" }} />}
          onClick={abrirModalNovaCidade}
          sx={ESTILO_BOTAO_ADICIONAR_CIDADE}
        >
          Adicionar Cidade
        </Button>
      </Box>

      {/* ERROS */}

      {erroCidades && (
        <Alert
          severity="error"
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={carregarDados}
              sx={botaoSalvar}
            >
              Tentar novamente
            </Button>
          }
          sx={{ mb: 1.5 }}
        >
          {erroCidades}
        </Alert>
      )}

      {!erroCidades && erroSres && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          As cidades foram carregadas, mas não foi possível consultar suas SREs
          vinculadas.
        </Alert>
      )}

      {/* BUSCA E REGISTROS POR PÁGINA */}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 1.25,
          px: 1.25,
          py: 0.55,
          mb: 0.5,
          border: "1px solid var(--sear-borda, #d5e7f7)",
          borderRadius: 1.5,
          backgroundColor: "var(--sear-superficie-azul, #f7fbff)",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            size="small"
            placeholder="Buscar cidade ou SRE..."
            value={busca}
            onChange={(event) => {
              setBusca(event.target.value);
              setPagina(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "var(--sear-azul, #087cf0)", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: 440 },
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                height: 34,
                backgroundColor: "var(--sear-superficie, #ffffff)",
                borderRadius: 1,
                "& fieldset": { borderColor: "var(--sear-borda, #d7e0ea)" },
                "&:hover fieldset": { borderColor: "var(--sear-borda, #a9c9e8)" },
              },
              "& .MuiInputBase-input": {
                fontSize: tamanhosControlesGlobais.campoBusca,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: "1px",
            height: 30,
            flexShrink: 0,
            backgroundColor: "var(--sear-superficie-azul, #d8e7f4)",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", md: "flex-end" },
            gap: 1.2,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--sear-texto-secundario, #667b98)",
              fontSize: tamanhosControlesGlobais.rotulo,
              whiteSpace: "nowrap",
            }}
          >
            Registros por página:
          </Typography>

          <Select
            size="small"
            value={itensPorPagina}
            onChange={(event) => {
              setItensPorPagina(Number(event.target.value));
              setPagina(1);
            }}
            renderValue={(valor) => `${valor} por página`}
            sx={{
              width: 160,
              height: 34,
              backgroundColor: "var(--sear-superficie, #ffffff)",
              color: "var(--sear-azul, #314d76)",
              borderRadius: 1,
              fontSize: tamanhosControlesGlobais.seletor,
              fontWeight: 500,
              "& .MuiSelect-select": { py: 0.55 },
            }}
          >
            {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
              <MenuItem key={quantidade} value={quantidade}>
                {quantidade} por página
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* PAGINAÇÃO SUPERIOR */}

      {!carregando && !erroCidades && cidadesFiltradas.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 0.75,
            px: 1.5,
            py: 0.35,
            mb: 0.5,
            border: "1px solid var(--sear-borda, #d7e8f8)",
            borderRadius: 1.5,
            backgroundColor: "var(--sear-superficie-azul, #f2f8fe)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--sear-texto-secundario, #536b8b)",
              fontSize: tamanhosControlesGlobais.contador,
            }}
          >
            {cidadesFiltradas.length}{" "}
            {cidadesFiltradas.length === 1 ? "registro" : "registros"}
          </Typography>

          <Box sx={{ ml: { sm: "auto" } }}>
            <PaginacaoLista
              paginaAtual={paginaAtual}
              totalPaginas={totalPaginas}
              onChange={setPagina}
              ariaLabel="Paginação superior da lista de cidades"
            />
          </Box>
        </Box>
      )}

      {/* CARREGAMENTO, VAZIO E TABELA */}

      {carregando ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !erroCidades && cidadesFiltradas.length === 0 ? (
        <Paper
          sx={{
            minHeight: 250,
            border: "1px solid var(--sear-borda, #d9e8fa)",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            py: 4,
            boxShadow: "none",
          }}
        >
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
              display: "grid",
              placeItems: "center",
              mb: 1.5,
            }}
          >
            <PlaceRounded sx={{ color: "var(--sear-azul, #087cf0)", fontSize: 38 }} />
          </Box>

          <Typography
            sx={{
              color: coresTextosGlobais.tituloSecao,
              fontSize: tamanhosPaginasAdministrativas.tituloSecao,
              fontWeight: 700,
            }}
          >
            {busca.trim()
              ? "Nenhuma cidade encontrada"
              : "Nenhuma cidade cadastrada"}
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              maxWidth: 390,
              mt: 0.5,
              mb: 2,
              fontSize: tamanhosControlesGlobais.descricao,
            }}
          >
            {busca.trim()
              ? "Nenhuma cidade ou SRE corresponde à sua busca."
              : "Ainda não há cidades cadastradas."}
          </Typography>

          {!busca.trim() && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddCircleRounded />}
              onClick={abrirModalNovaCidade}
              sx={ESTILO_BOTAO_ADICIONAR_CIDADE}
            >
              Criar primeira cidade
            </Button>
          )}
        </Paper>
      ) : !erroCidades ? (
        <>
          <TableContainer
            data-rolagem-conteudo=""
            component={Paper}
            sx={{
              borderRadius: 1.5,
              border: "1px solid var(--sear-borda, #d7e7f7)",
              boxShadow: "none",
              overflow: "hidden",
            }}
          >
            <Table
              size="small"
              sx={{
                minWidth: 720,
                tableLayout: "fixed",
                "& .MuiTableCell-root": { borderColor: "var(--sear-borda, #dfe9f3)" },
              }}
            >
              <TableHead sx={{ backgroundColor: "var(--sear-superficie-azul, #edf6fe)" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "40%",
                      px: 2,
                      py: 0.55,
                      borderRight: "1px solid var(--sear-borda, #d9e7f3)",
                    }}
                  >
                    {renderCabecalhoOrdenavel("Cidade", "cidade", PlaceRounded)}
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "44%",
                      px: 2,
                      py: 0.55,
                      borderRight: "1px solid var(--sear-borda, #d9e7f3)",
                    }}
                  >
                    {renderCabecalhoOrdenavel(
                      "SRE vinculada",
                      "sre",
                      BuildingsFillIcon,
                    )}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ width: "16%", px: 1, py: 0.55 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.7,
                        color: "var(--sear-azul, #27456f)",
                      }}
                    >
                      <SettingsRounded
                        sx={{
                          color: "var(--sear-azul, #087cf0)",
                          fontSize: iconesGlobais.tabela.fontSize,
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{
                          color: coresTextosGlobais.cabecalhoTabela,
                          fontSize:
                            tamanhosPaginasAdministrativas.cabecalhoTabela,
                          fontWeight: 700,
                        }}
                      >
                        Ações
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {cidadesDaPagina.map((cidade) => {
                  const nomesSres = sresPorCidade.get(Number(cidade.id)) || [];

                  return (
                    <TableRow
                      key={cidade.id}
                      hover
                      sx={{
                        height: 38,
                        "&:hover": { backgroundColor: "var(--sear-superficie, #fbfdff)" },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell
                        sx={{
                          px: 2,
                          py: 0.35,
                          borderRight: "1px solid var(--sear-borda, #e5edf5)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                            minWidth: 0,
                          }}
                        >
                          <PlaceRounded
                            sx={{
                              color: "var(--sear-azul, #087cf0)",
                              fontSize: iconesGlobais.conteudoTabela.fontSize,
                              flexShrink: 0,
                            }}
                          />

                          <Tooltip title={cidade.name} arrow describeChild>
                            <Typography
                              
                              sx={{
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: coresTextosGlobais.link,
                                fontSize:
                                  tamanhosPaginasAdministrativas.conteudoTabela,
                                fontWeight: 600,
                              }}
                            >
                              {cidade.name}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell
                        sx={{
                          px: 2,
                          py: 0.35,
                          borderRight: "1px solid var(--sear-borda, #e5edf5)",
                        }}
                      >
                        <Tooltip title={
                            erroSres
                              ? "Indisponível"
                              : nomesSres.length
                                ? nomesSres.join(", ")
                                : "Não vinculada"
                          } arrow describeChild>
                          <Typography
                            
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: coresTextosGlobais.conteudoTabela,
                              fontSize:
                                tamanhosPaginasAdministrativas.conteudoTabela,
                            }}
                          >
                            {erroSres
                              ? "Indisponível"
                              : nomesSres.length
                                ? nomesSres.join(", ")
                                : "Não vinculada"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{ px: 1, py: 0.35, whiteSpace: "nowrap" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 0.85,
                          }}
                        >
                          <Tooltip title="Editar" arrow>
                            <IconButton
                              size="small"
                              
                              aria-label={`Editar ${cidade.name}`}
                              onClick={() => abrirEdicao(cidade)}
                              sx={ESTILO_ACAO_EDITAR}
                            >
                              <EditRounded
                                sx={{ fontSize: iconesGlobais.tabela.fontSize }}
                              />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Excluir" arrow>
                            <IconButton
                              size="small"
                              
                              aria-label={`Excluir ${cidade.name}`}
                              onClick={() => {
                                setCidadeParaExcluir(cidade);
                                setConfirmacaoExclusaoAberta(true);
                              }}
                              sx={ESTILO_ACAO_EXCLUIR}
                            >
                              <DeleteRounded
                                sx={{ fontSize: iconesGlobais.tabela.fontSize }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 0.75,
              mt: 0.45,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "var(--sear-texto-secundario, #526b8d)",
                fontSize: tamanhosControlesGlobais.contador,
              }}
            >
              {cidadesFiltradas.length
                ? `Mostrando ${indiceInicial + 1} a ${indiceFinal} de ${
                    cidadesFiltradas.length
                  } resultados`
                : "Mostrando 0 resultados"}
            </Typography>

            <Box sx={{ ml: { sm: "auto" } }}>
              <PaginacaoLista
                paginaAtual={paginaAtual}
                totalPaginas={totalPaginas}
                onChange={setPagina}
                ariaLabel="Paginação inferior da lista de cidades"
              />
            </Box>
          </Box>
        </>
      ) : null}

      {/* NOVA CIDADE */}

      <Dialog
        open={modalNovaCidade}
        onClose={fecharModalNovaCidade}
        slotProps={{
          transition: {
            onExited: () => {
              setNomeNovaCidade("");
              setSreSelecionada("");
              setErroFormulario("");
            },
          },
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: ESTILO_PAPEL_MODAL }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            px: 2.5,
            pt: 2,
            pb: 1.4,
            pr: 6,
          }}
        >
          <Box sx={ESTILO_ICONE_TITULO_MODAL}>
            <PlaceRounded sx={{ fontSize: 25 }} />
          </Box>

          <Box>
            <Typography
              component="h2"
              sx={{
                color: "var(--sear-azul, #0767d8)",
                fontSize: tamanhosModaisGlobais.tituloModal,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              Nova cidade
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: tamanhosModaisGlobais.descricao,
                mt: 0.35,
                lineHeight: 1.4,
              }}
            >
              Cadastre uma nova cidade para disponibilizá-la para vinculação às
              SREs.
            </Typography>
          </Box>

          <IconButton
            aria-label="Fechar"
            size="small"
            onClick={fecharModalNovaCidade}
            disabled={salvando}
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              color: "var(--sear-texto-secundario, #40597c)",
              "&:hover": {
                color: "var(--sear-azul, #0767d8)",
                backgroundColor: "var(--sear-superficie-azul, #eef6ff)",
              },
            }}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Box component="form" onSubmit={criarCidade}>
          <DialogContent sx={{ pt: 0.8, px: 2 }}>
            <Box sx={ESTILO_CARD_MODAL}>
              <Box sx={ESTILO_CABECALHO_CARD_MODAL}>
                <Box sx={ESTILO_ICONE_CARD_MODAL}>
                  <LocationCityRounded sx={{ fontSize: 23 }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: "var(--sear-azul, #0767d8)",
                      fontSize: tamanhosModaisGlobais.tituloSecao,
                      fontWeight: 700,
                    }}
                  >
                    Dados da cidade
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.2,
                      color: "var(--sear-texto-secundario, #6b7f95)",
                      fontSize: tamanhosModaisGlobais.descricao,
                    }}
                  >
                    Informações para cadastro da cidade.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ px: 1.75, py: 1.6, display: "grid", gap: 1.45 }}>
                <TextField
                  autoFocus
                  fullWidth
                  required
                  size="small"
                  label="Nome da cidade"
                  placeholder="Ex.: Uberlândia"
                  value={nomeNovaCidade}
                  onChange={(event) => {
                    setNomeNovaCidade(event.target.value);
                    setErroFormulario("");
                  }}
                  disabled={salvando}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityRounded
                          sx={{ color: "var(--sear-texto-secundario, #64748b)", fontSize: 18 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={ESTILO_CAMPO_MODAL}
                />

                <FormControl
                  fullWidth
                  size="small"
                  disabled={salvando || erroSres}
                  sx={ESTILO_CAMPO_MODAL}
                >
                  <InputLabel id="sre-nova-cidade-label">
                    SRE vinculada (opcional)
                  </InputLabel>

                  <Select
                    labelId="sre-nova-cidade-label"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": {
                            fontSize: tamanhosModaisGlobais.opcaoSelecao,
                          },
                        },
                      },
                    }}
                    value={sreSelecionada}
                    label="SRE vinculada (opcional)"
                    onChange={(event) => setSreSelecionada(event.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <BuildingsFillIcon
                          sx={{ color: "var(--sear-texto-secundario, #64748b)", fontSize: 18 }}
                        />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">Sem vínculo por enquanto</MenuItem>

                    {sresAtivas.map((sre) => (
                      <MenuItem key={sre.id} value={sre.id}>
                        {sre.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.7,
                  }}
                >
                  <InfoRounded
                    sx={{
                      mt: 0.05,
                      color: "var(--sear-texto-suave, #7f93aa)",
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "var(--sear-texto-secundario, #71869d)",
                      fontSize: tamanhosModaisGlobais.textoAuxiliar,
                      lineHeight: 1.4,
                    }}
                  >
                    O vínculo com a SRE é opcional e poderá ser alterado
                    posteriormente.
                  </Typography>
                </Box>

                {erroSres && (
                  <Alert severity="warning">
                    Não foi possível carregar as SREs. A cidade pode ser
                    cadastrada sem vínculo.
                  </Alert>
                )}

                {erroFormulario && (
                  <Alert severity="error">{erroFormulario}</Alert>
                )}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 1,
              borderTop: "1px solid var(--sear-borda, #e3edf6)",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={fecharModalNovaCidade}
              disabled={salvando}
              sx={{
                ...botaoCancelar,
                fontSize: tamanhosModaisGlobais.botao,
              }}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              variant="outlined"
              disabled={salvando || nomeNovaCidade.trim().length < 2}
              sx={{
                ...botaoSalvar,
                fontSize: tamanhosModaisGlobais.botao,
              }}
            >
              {salvando ? "Salvando..." : "Criar cidade"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* EDITAR CIDADE */}

      <Dialog
        open={modalEdicaoAberto}
        onClose={fecharEdicao}
        maxWidth="xs"
        fullWidth
        aria-labelledby="editar-cidade-titulo"
        slotProps={{
          transition: {
            onExited: () => {
              setCidadeParaEditar(null);
              setNomeEdicao("");
              setSreEdicaoSelecionada("");
              setErroEdicao("");
            },
          },
        }}
        PaperProps={{ sx: ESTILO_PAPEL_MODAL }}
      >
        <DialogTitle
          id="editar-cidade-titulo"
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            px: 2.5,
            pt: 2,
            pb: 1.4,
            pr: 6,
          }}
        >
          <Box sx={ESTILO_ICONE_TITULO_MODAL}>
            <PlaceRounded sx={{ fontSize: 25 }} />
          </Box>

          <Box>
            <Typography
              component="h2"
              sx={{
                color: "var(--sear-azul, #0767d8)",
                fontSize: tamanhosModaisGlobais.tituloModal,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              Editar cidade
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: tamanhosModaisGlobais.descricao,
                mt: 0.35,
                lineHeight: 1.4,
              }}
            >
              Atualize os dados da cidade para mantê-la disponível para
              vinculação às SREs.
            </Typography>
          </Box>

          <IconButton
            aria-label="Fechar"
            size="small"
            onClick={fecharEdicao}
            disabled={salvandoEdicao}
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              color: "var(--sear-texto-secundario, #40597c)",
              "&:hover": {
                color: "var(--sear-azul, #0767d8)",
                backgroundColor: "var(--sear-superficie-azul, #eef6ff)",
              },
            }}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Box component="form" onSubmit={salvarEdicao}>
          <DialogContent sx={{ pt: 0.8, px: 2 }}>
            <Box sx={ESTILO_CARD_MODAL}>
              <Box sx={ESTILO_CABECALHO_CARD_MODAL}>
                <Box sx={ESTILO_ICONE_CARD_MODAL}>
                  <PlaceRounded sx={{ fontSize: 23 }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: "var(--sear-azul, #0767d8)",
                      fontSize: tamanhosModaisGlobais.tituloSecao,
                      fontWeight: 700,
                    }}
                  >
                    Dados da cidade
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.2,
                      color: "var(--sear-texto-secundario, #6b7f95)",
                      fontSize: tamanhosModaisGlobais.descricao,
                    }}
                  >
                    Informações da cidade para vinculação às SREs.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ px: 1.75, py: 1.6, display: "grid", gap: 1.45 }}>
                <TextField
                  autoFocus
                  fullWidth
                  required
                  size="small"
                  label="Nome da cidade"
                  value={nomeEdicao}
                  disabled={salvandoEdicao}
                  onChange={(event) => {
                    setNomeEdicao(event.target.value);
                    setErroEdicao("");
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityRounded
                          sx={{ color: "var(--sear-texto-secundario, #64748b)", fontSize: 18 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={ESTILO_CAMPO_MODAL}
                />

                <FormControl
                  fullWidth
                  size="small"
                  disabled={salvandoEdicao || erroSres}
                  sx={ESTILO_CAMPO_MODAL}
                >
                  <InputLabel id="sre-edicao-cidade-label">
                    SRE vinculada (opcional)
                  </InputLabel>

                  <Select
                    labelId="sre-edicao-cidade-label"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": {
                            fontSize: tamanhosModaisGlobais.opcaoSelecao,
                          },
                        },
                      },
                    }}
                    value={sreEdicaoSelecionada}
                    label="SRE vinculada (opcional)"
                    onChange={(event) => {
                      setSreEdicaoSelecionada(event.target.value);
                      setErroEdicao("");
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <BuildingsFillIcon
                          sx={{ color: "var(--sear-texto-secundario, #64748b)", fontSize: 18 }}
                        />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">Sem vínculo</MenuItem>

                    {sresAtivas.map((sre) => (
                      <MenuItem key={sre.id} value={sre.id}>
                        {sre.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.7,
                  }}
                >
                  <InfoRounded
                    sx={{
                      mt: 0.05,
                      color: "var(--sear-texto-suave, #7f93aa)",
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "var(--sear-texto-secundario, #71869d)",
                      fontSize: tamanhosModaisGlobais.textoAuxiliar,
                      lineHeight: 1.4,
                    }}
                  >
                    O vínculo com a SRE pode ser alterado a qualquer momento.
                  </Typography>
                </Box>

                {erroEdicao && <Alert severity="error">{erroEdicao}</Alert>}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 1,
              borderTop: "1px solid var(--sear-borda, #e3edf6)",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={fecharEdicao}
              disabled={salvandoEdicao}
              sx={{
                ...botaoCancelar,
                fontSize: tamanhosModaisGlobais.botao,
              }}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              variant="outlined"
              disabled={salvandoEdicao || nomeEdicao.trim().length < 2}
              sx={{
                ...botaoSalvar,
                fontSize: tamanhosModaisGlobais.botao,
              }}
            >
              {salvandoEdicao ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* CONFIRMAÇÃO DE EXCLUSÃO */}

      <Dialog
        open={confirmacaoExclusaoAberta}
        onClose={() => !excluindo && setConfirmacaoExclusaoAberta(false)}
        slotProps={{
          transition: {
            onExited: () => setCidadeParaExcluir(null),
          },
        }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="excluir-cidade-titulo"
        aria-describedby="excluir-cidade-descricao"
        PaperProps={{
          sx: {
            borderRadius: 2,
            borderTop: "5px solid var(--sear-erro-borda, #d32f2f)",
            maxWidth: 440,
          },
        }}
      >
        <DialogTitle
          id="excluir-cidade-titulo"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "var(--sear-erro-texto, #c62828)",
            fontSize: tamanhosModaisGlobais.tituloModal,
            fontWeight: 700,
          }}
        >
          <WarningAmberRounded sx={{ fontSize: 26 }} />
          Excluir cidade?
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Typography
            id="excluir-cidade-descricao"
            sx={{
              color: "var(--sear-texto, #334155)",
              fontSize: tamanhosModaisGlobais.descricao,
            }}
          >
            Tem certeza de que deseja excluir{" "}
            <strong>{cidadeParaExcluir?.name}</strong>? Essa ação não pode ser
            desfeita.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => setConfirmacaoExclusaoAberta(false)}
            disabled={excluindo}
            sx={{
              ...botaoCancelar,
              fontSize: tamanhosModaisGlobais.botao,
            }}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            variant="outlined"
            onClick={excluirCidade}
            disabled={excluindo}
            sx={{
              ...botaoExcluir,
              fontSize: tamanhosModaisGlobais.botao,
            }}
          >
            {excluindo ? "Excluindo..." : "Excluir cidade"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FEEDBACK */}

      <Snackbar
        open={aviso.open}
        autoHideDuration={4000}
        onClose={() =>
          setAviso((atual) => ({
            ...atual,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={aviso.severity}
          onClose={() =>
            setAviso((atual) => ({
              ...atual,
              open: false,
            }))
          }
        >
          {aviso.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
